import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import { useFetchEquityDetails, useFetchHistoricalPrice } from '@/trading/tradingQueries'
import { getPriceRange } from '@/common/utils/utils'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import { APP } from '@/common/strings'
import { SecurityDetailsPageHeader } from './SecurityDetailsPageHeader.tsx'
import { SecurityPriceAndGraph } from './SecurityPriceAndGraph.tsx'
import { BuildingIcon, CoinsIcon, SproutIcon } from 'lucide-react'
import { useEsgProfile, useEsgTerms } from '@/research/researchQueries.ts'
import { FinancialProfile } from './financialProfile'
import { EthicalProfile } from './EthicalProfile.tsx'
import { analytics } from '@/common/analytics/analytics'
import { useFetchCampaignsList } from '@/campaigns/campaignsQueries.ts'
import { CampaignT } from '@/common/types.ts'
import CompanyProfile from './CompanyProfile.tsx'
import { NotFoundError } from '@/common/api/error.ts'

function Tabs({
    onSelectionChange,
    selectedTab,
    tabLabels,
    securityCode,
}: {
    onSelectionChange: (label: any) => void
    selectedTab: any
    tabLabels: any
    securityCode: string
}) {
    return (
        <div className="flex rounded-full w-full border-neutral-200 mb-4 border flex-row p-1">
            {tabLabels.map((label: any, index: number) => (
                <button
                    key={label.text ?? index}
                    type="button"
                    className={`${2 === index ? '' : 'border-r '} w-full p-1`}
                    onClick={() => {
                        onSelectionChange(label)
                        analytics.track(`${label.text.replace(' ', '')} Viewed`, { securityCode })
                    }}
                >
                    <div
                        className={`w-full p-2.5 ${
                            selectedTab?.text === label.text ? 'bg-[#ECEFE9]' : 'hover:bg-stone-100'
                        }  rounded-[100px] justify-center items-center gap-1 flex`}
                    >
                        {label.icon} {label.text}
                    </div>
                </button>
            ))}
        </div>
    )
}

const tabLabels = [
    {
        text: 'Ethical Profile',
        icon: <SproutIcon />,
    },
    {
        text: 'Financial Profile',
        icon: <CoinsIcon />,
    },
    {
        text: 'Company Profile',
        icon: <BuildingIcon />,
    },
]

export const SecurityDetails = () => {
    const { securityCode } = useParams()

    const { data: equity, error: equityError } = useFetchEquityDetails(securityCode)
    const { data: profile } = useEsgProfile(securityCode)
    const { data: terms } = useEsgTerms()
    const { data: yearPricePoints, error: historicalPricesError } = useFetchHistoricalPrice(equity?.securityCode, '1Y')
    const { data: campaigns } = useFetchCampaignsList()

    const [selectedTab, setSelectedTab] = useState(tabLabels[0])
    const [campaignsForSecurity, setCampaignsForSecurity] = useState<CampaignT[]>()
    const yearRange = useMemo(
        () => (yearPricePoints && yearPricePoints.length > 0 ? getPriceRange(yearPricePoints) : null),
        [yearPricePoints],
    )

    useEffect(() => {
        analytics.track(`${selectedTab.text} tab clicked`)
    }, [selectedTab, setSelectedTab])

    useEffect(() => {
        const applicableCampaigns = campaigns?.campaigns.filter((campaign) => {
            return (
                campaign.targetCompaniesAsxCodes.find((targetCode) => equity?.securityCode === targetCode) !== undefined
            )
        })
        setCampaignsForSecurity(applicableCampaigns)
    }, [campaigns, equity?.securityCode, setCampaignsForSecurity])

    if (equityError instanceof NotFoundError || historicalPricesError instanceof NotFoundError) return null

    if (!securityCode || !equity) return <FullScreenLoader className="h-20" />

    return (
        <>
            <Helmet>
                <title>
                    {securityCode} | {APP.title}
                </title>
            </Helmet>
            <div className="top-0 bg-white">
                <SecurityDetailsPageHeader securityName={equity?.description} />
            </div>
            <main className="flex flex-col gap-8">
                <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-8">
                    <SecurityPriceAndGraph securityCode={securityCode} />
                </div>

                <Tabs
                    tabLabels={tabLabels}
                    onSelectionChange={setSelectedTab}
                    selectedTab={selectedTab}
                    securityCode={securityCode}
                />
                {selectedTab.text === 'Ethical Profile' && (
                    <EthicalProfile
                        data={profile?.data}
                        campaignsForSecurity={campaignsForSecurity ?? []}
                        securityCode={securityCode}
                        dictionary={terms?.data ?? []}
                    />
                )}
                {selectedTab.text === 'Financial Profile' && (
                    <FinancialProfile yearRange={yearRange} statistics={equity?.statistics} />
                )}
                {selectedTab.text === 'Company Profile' && <CompanyProfile data={equity.about} />}
            </main>
        </>
    )
}
